/**
 * Questo file definisce il componente radice dell'applicazione, `App`.
 * - È responsabile della configurazione del sistema di routing principale utilizzando React Router.
 * - Definisce tutte le rotte dell'applicazione, associando ogni percorso URL a un componente pagina specifico.
 * - Utilizza componenti "guardiano" (ProtectedRoute, PublicRoute, AdminRoute) per proteggere le rotte
 * e gestire l'accesso in base allo stato di autenticazione e al ruolo dell'utente.
 * - Al primo montaggio, avvia un controllo per verificare lo stato di autenticazione dell'utente
 * dispatchando un'azione Redux, garantendo che lo stato dell'app sia sincronizzato.
 */

import React, { useEffect } from 'react';
// Importa i componenti necessari da React Router per gestire il routing.
import { BrowserRouter, Routes, Route } from 'react-router-dom';
// Importa il hook useDispatch di Redux per poter inviare azioni.
import { useDispatch } from 'react-redux';
// Importa l'azione per controllare lo stato di autenticazione.
import { checkAuthStatus } from './redux/authSlice';

// Import dei componenti Pagina che verranno associati alle rotte.
import Home from './pages/Home';
import Register from './pages/Register';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Account from './pages/Account';
import CreateFlashcards from './pages/CreateFlashcards';
import MyFlashcards from './pages/MyFlashcards';
import AdminDashboard from './pages/AdminDashboard';
import PublicFolders from './pages/PublicFolders';
import FolderDetailPage from './pages/FolderDetailPage';
import StudySession from './pages/StudySession';
import PublicStudySession from './pages/PublicStudySession';

// Import dei componenti "guardiano" per le rotte.
import ProtectedRoute from './auth/ProtectedRoute'; // Per utenti autenticati.
import PublicRoute from './auth/PublicRoute';     // Per utenti non autenticati.
import AdminRoute from './auth/AdminRoute';       // Per utenti con ruolo 'admin'.

/**
 * Componente funzionale App.
 * @returns {React.ReactElement} - Il JSX che definisce la struttura di routing dell'intera applicazione.
 */
function App() {
  // Ottiene la funzione `dispatch` dallo store di Redux.
  const dispatch = useDispatch();

  // useEffect viene eseguito una sola volta quando il componente App viene montato.
  // È il posto ideale per avviare operazioni iniziali come il controllo dell'autenticazione.
  useEffect(() => {
    // Fa il dispatch dell'azione asincrona `checkAuthStatus` per verificare se l'utente
    // ha una sessione valida (es. tramite cookie). Questo aggiornerà lo stato di Redux.
    dispatch(checkAuthStatus());
  }, [dispatch]); // L'array di dipendenze con `dispatch` assicura che l'effetto venga eseguito solo una volta.

  return (
    // `BrowserRouter` è il componente che abilita il routing basato su URL nel browser.
    <BrowserRouter>
      {/* `Routes` è il contenitore per tutte le definizioni di rotte. */}
      <Routes>
        {/* --- Rotte Pubbliche (accessibili a tutti) --- */}
        <Route path="/" element={<Home />} />
        <Route path="/public" element={<PublicFolders />} />
        <Route path="/public/folder/:folderId" element={<FolderDetailPage />} />
        <Route path="/public/study/:folderId" element={<PublicStudySession />} />

        {/* --- Rotte per Non Autenticati (es. Login, Registrazione) --- */}
        {/* `PublicRoute` impedisce a un utente già loggato di vedere queste pagine. */}
        <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
        <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />

        {/* --- Rotte Protette (solo per utenti autenticati) --- */}
        {/* `ProtectedRoute` reindirizza al login se l'utente non è autenticato. */}
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/account" element={<ProtectedRoute><Account /></ProtectedRoute>} />
        <Route path="/create" element={<ProtectedRoute><CreateFlashcards /></ProtectedRoute>} />
        <Route path="/my-flashcards" element={<ProtectedRoute><MyFlashcards /></ProtectedRoute>} />
        <Route path="/study/folder/:folderId" element={<ProtectedRoute><StudySession /></ProtectedRoute>} />

        {/* --- Rotta Amministrativa (solo per admin autenticati) --- */}
        {/* La protezione è doppia: `ProtectedRoute` per l'autenticazione e `AdminRoute` per il ruolo. */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <AdminRoute>
                <AdminDashboard />
              </AdminRoute>
            </ProtectedRoute>
          }
        />
      </Routes>
    </BrowserRouter>
  );
}

// Esporta il componente App per essere renderizzato in `index.js`.
export default App;